import { useEffect, useState, useCallback } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, AlertTriangle, CalendarClock, Wallet, Euro, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

const AGING_LABELS = [
  ["no_vencido", "No vencido", "text-slate-600"],
  ["1_30", "1-30 días", "text-amber-600"],
  ["31_60", "31-60 días", "text-orange-600"],
  ["61_90", "61-90 días", "text-red-500"],
  ["mas_90", "+90 días", "text-red-700"],
];

const SORTERS = {
  supplier_name: (i) => (i.supplier_name || "").toLowerCase(),
  invoice_number: (i) => (i.invoice_number || "").toLowerCase(),
  invoice_date: (i) => i.invoice_date || "",
  due_date: (i) => i.due_date || "9999-12-31",
  days_overdue: (i) => i.days_overdue || 0,
  branch_name: (i) => i.branch_name || "",
  total: (i) => i.total || 0,
  paid_amount: (i) => i.paid_amount || 0,
  pending: (i) => i.pending || 0,
  payment_status: (i) => i.payment_status || "",
};

function SortTh({ k, sort, setSort, className = "", children }) {
  return (
    <TableHead data-testid={`ap-sort-${k}`}
      className={`cursor-pointer select-none hover:text-slate-900 transition-colors ${className}`}
      onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))}>
      <span className="inline-flex items-center gap-1">
        {children}
        {sort.key === k
          ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
      </span>
    </TableHead>
  );
}

export default function CuentasPagar() {
  const { branchFilter, user } = useAuth();
  const [data, setData] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("efectivo");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [search, setSearch] = useState("");
  const [fBranch, setFBranch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fDue, setFDue] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const load = useCallback(() => {
    const p = branchFilter ? `?branch_id=${branchFilter}` : "";
    api.get(`/payables${p}`).then((r) => setData(r.data)).catch(() => {});
  }, [branchFilter]);

  useEffect(() => { load(); }, [load]);

  const registerPayment = async () => {
    const amount = Number(payAmount);
    if (!amount || amount <= 0) {
      toast.error("Importe inválido");
      return;
    }
    try {
      const r = await api.post(`/payables/${payTarget.invoice_id}/payments`, { amount, date: payDate, method: payMethod });
      toast.success(`Pago registrado — estado: ${r.data.payment_status}, pendiente: ${fmtEUR(r.data.pending)}`);
      setPayTarget(null);
      setPayAmount("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar el pago");
    }
  };

  const k = data?.kpis || {};
  const branchNames = [...new Set((data?.items || []).map((i) => i.branch_name).filter(Boolean))].sort();
  const filtered = (data?.items || [])
    .filter((i) => !search || (i.supplier_name || "").toLowerCase().includes(search.toLowerCase())
      || (i.invoice_number || "").toLowerCase().includes(search.toLowerCase()))
    .filter((i) => !fBranch || i.branch_name === fBranch)
    .filter((i) => !fStatus || i.payment_status === fStatus)
    .filter((i) => !fDue || (fDue === "vencidas" ? i.days_overdue > 0 : i.days_overdue <= 0));
  const items = sort.key
    ? [...filtered].sort((a, b) => {
        const va = SORTERS[sort.key](a);
        const vb = SORTERS[sort.key](b);
        const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
        return sort.dir === "asc" ? cmp : -cmp;
      })
    : filtered;

  return (
    <div className="space-y-6" data-testid="cuentas-pagar-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Cuentas por pagar</h1>
        <p className="text-sm text-slate-500 mt-1">Facturas validadas pendientes de pago, con vencimientos y aging</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total pendiente", value: fmtEUR(k.total_pendiente), icon: Wallet, testid: "ap-total", cls: "" },
          { label: "Total vencido", value: fmtEUR(k.total_vencido), icon: AlertTriangle, testid: "ap-overdue", cls: "text-red-600" },
          { label: "Vence en 7 días", value: fmtEUR(k.vence_7d), icon: Clock, testid: "ap-7d", cls: "text-amber-600" },
          { label: "Vence en 30 días", value: fmtEUR(k.vence_30d), icon: CalendarClock, testid: "ap-30d", cls: "" },
        ].map((c) => (
          <Card key={c.testid} data-testid={c.testid}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">{c.label}</CardTitle>
              <c.icon className="w-4 h-4 text-slate-400" />
            </CardHeader>
            <CardContent><div className={`text-2xl font-extrabold font-num tracking-tight ${c.cls}`}>{c.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="ap-aging">
        <CardHeader><CardTitle className="text-base font-semibold font-heading">Aging de la deuda</CardTitle></CardHeader>
        <CardContent className="flex gap-6 flex-wrap">
          {AGING_LABELS.map(([key, label, cls]) => (
            <div key={key} data-testid={`aging-${key}`}>
              <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
              <div className={`font-num font-bold text-lg ${cls}`}>{fmtEUR((data?.aging || {})[key] ?? 0)}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base font-semibold font-heading" data-testid="ap-count">{items.length} facturas pendientes</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Input data-testid="ap-search" placeholder="Buscar proveedor o nº..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="w-56 h-9" />
            <Select value={fBranch || "all"} onValueChange={(v) => setFBranch(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="ap-filter-branch" className="w-44 h-9"><SelectValue placeholder="Sucursal" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="ap-filter-branch-all">Todas las sucursales</SelectItem>
                {branchNames.map((b) => <SelectItem key={b} value={b} data-testid={`ap-filter-branch-${b}`}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fStatus || "all"} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="ap-filter-status" className="w-36 h-9"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="ap-filter-status-all">Todos</SelectItem>
                <SelectItem value="pendiente" data-testid="ap-filter-status-pendiente">Pendiente</SelectItem>
                <SelectItem value="parcial" data-testid="ap-filter-status-parcial">Parcial</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fDue || "all"} onValueChange={(v) => setFDue(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="ap-filter-due" className="w-36 h-9"><SelectValue placeholder="Vencimiento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="ap-filter-due-all">Todas</SelectItem>
                <SelectItem value="vencidas" data-testid="ap-filter-due-vencidas">Vencidas</SelectItem>
                <SelectItem value="por_vencer" data-testid="ap-filter-due-por-vencer">Por vencer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortTh k="supplier_name" sort={sort} setSort={setSort}>Proveedor</SortTh>
                <SortTh k="invoice_number" sort={sort} setSort={setSort}>Factura</SortTh>
                <SortTh k="invoice_date" sort={sort} setSort={setSort}>Fecha</SortTh>
                <SortTh k="due_date" sort={sort} setSort={setSort}>Vencimiento</SortTh>
                <SortTh k="days_overdue" sort={sort} setSort={setSort} className="text-right">Días vencida</SortTh>
                <SortTh k="branch_name" sort={sort} setSort={setSort}>Sucursal</SortTh>
                <SortTh k="total" sort={sort} setSort={setSort} className="text-right">Total</SortTh>
                <SortTh k="paid_amount" sort={sort} setSort={setSort} className="text-right">Pagado</SortTh>
                <SortTh k="pending" sort={sort} setSort={setSort} className="text-right">Pendiente</SortTh>
                <SortTh k="payment_status" sort={sort} setSort={setSort}>Estado</SortTh>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.invoice_id} data-testid={`ap-row-${i.invoice_id}`}>
                  <TableCell className="text-sm font-medium max-w-44 truncate">{i.supplier_name}</TableCell>
                  <TableCell className="font-num text-xs">{i.invoice_number || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(i.invoice_date)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(i.due_date)}</TableCell>
                  <TableCell className={`text-right font-num font-semibold ${i.days_overdue > 0 ? "text-red-600" : "text-slate-400"}`}>
                    {i.days_overdue > 0 ? i.days_overdue : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{i.branch_name || "—"}</TableCell>
                  <TableCell className="text-right font-num">{fmtEUR(i.total)}</TableCell>
                  <TableCell className="text-right font-num text-slate-500">{fmtEUR(i.paid_amount)}</TableCell>
                  <TableCell className="text-right font-num font-semibold text-amber-700">{fmtEUR(i.pending)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={i.payment_status === "parcial"
                      ? "text-blue-700 border-blue-200 bg-blue-50" : "text-amber-700 border-amber-200 bg-amber-50"}>
                      {i.payment_status === "parcial" ? "Parcial" : "Pendiente"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(user?.role === "ADMIN" || user?.role === "FINANCE") && (
                      <Button data-testid={`ap-pay-${i.invoice_id}`} size="sm" variant="outline" className="gap-1"
                        onClick={() => { setPayTarget(i); setPayAmount(String(i.pending)); }}>
                        <Euro className="w-3.5 h-3.5" /> Pago
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={11} className="text-center py-10 text-slate-400 text-sm">
                  Sin facturas que coincidan con los filtros.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="pay-dialog">
          <DialogHeader><DialogTitle className="font-heading">Registrar pago</DialogTitle>
            <DialogDescription>Registra un pago total o parcial de la factura. Se puede reversar después.</DialogDescription>
          </DialogHeader>
          {payTarget && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-slate-600">
                <strong>{payTarget.supplier_name}</strong> · {payTarget.invoice_number || "s/n"} · pendiente {fmtEUR(payTarget.pending)}
              </div>
              <div className="space-y-1.5">
                <Label>Importe</Label>
                <Input data-testid="pay-amount-input" type="number" step="0.01" value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Fecha</Label>
                <Input data-testid="pay-date-input" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Método de pago</Label>
                <div className="text-sm border border-slate-200 rounded-md px-3 py-2 bg-slate-50 font-medium" data-testid="pay-method-fixed">Efectivo</div>
                <p className="text-xs text-slate-500">El registro manual solo es válido en efectivo — los pagos por tarjeta o cuenta bancaria se concilian con el estado de cuenta desde Conciliación.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPayTarget(null)}>Cancelar</Button>
                <Button data-testid="pay-confirm-button" onClick={registerPayment}
                  className="bg-[#D9381E] hover:bg-[#B91C1C] text-white">Registrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
